import React, { useState, useCallback } from 'react';
import { FiSearch, FiChevronDown, FiChevronRight, FiStar, FiLock, FiRefreshCw, FiTrash2, FiX, FiLoader, FiClock, FiZap, FiPlus, FiUpload } from 'react-icons/fi';
import { PageHeader, Skeleton, Notice, Kpi } from '../Components/UI';
import { useCachedQuery } from '../Components/Hooks/useCachedQuery';
import { fetchMoonrock, postMoonrock } from '../Utils/MoonrockAPI';

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

type Stage = { stage: number; image: string };
type Creature = {
  creatureId: string;
  name: string;
  element: string;
  planet: string;
  family: string;
  starter: boolean;
  maxStage: number;
  wormholeWeight: number;
  stageNames: Record<string, string>;
  evolvesAtLevels: number[];
  stages: Stage[];
};

type Producer = {
  group: string;
  isPlanet: boolean;
  unlockLevel: number | null;
  levels: number;
};

type MergeItem = {
  type: string;
  name: string;
  level: number;
  image: string | null;
};

type ItemGroup = {
  group: string;
  normal: MergeItem[];
  alternative: MergeItem[];
};

type BuildItem = {
  id: string;
  title: string;
  image: string;
  price: number;
  coins: number;
  planetId: string;
  producerLevel: number;
  footprint: number;
  isNew: boolean;
};

type BuildCategory = {
  id: string;
  name: string;
  items: BuildItem[];
};

type GameAssets = {
  creatures: Creature[];
  producers: Producer[];
  planets: string[];
  producerGroups: string[];
  itemGroups: ItemGroup[];
  buildCatalog: BuildCategory[];
};

type AssetVersion = {
  key: string;
  url: string;
  timestamp: number;
  size: number;
};

/* ------------------------------------------------------------------ */
/*  Tabs                                                              */
/* ------------------------------------------------------------------ */

type Tab = 'creatures' | 'producers' | 'items' | 'build';
const TABS: { key: Tab; label: string }[] = [
  { key: 'creatures', label: 'Lunas' },
  { key: 'producers', label: 'Producers' },
  { key: 'items', label: 'Merge Items' },
  { key: 'build', label: 'Build Island' },
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

const IMG_FALLBACK = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64"><rect width="64" height="64" rx="8" fill="%23e2e8f0"/><text x="32" y="36" text-anchor="middle" font-size="10" fill="%2394a3b8">?</text></svg>';

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const s3KeyFromUrl = (url: string) => {
  const m = url.match(/s3\.us-east-2\.amazonaws\.com\/([^?]+)/);
  return m ? m[1] : url.split('?')[0];
};

const formatDate = (ts: number) => {
  const d = new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return bytes + ' B';
  return (bytes / 1024).toFixed(0) + ' KB';
};

const PLANET_COLORS: Record<string, string> = {
  dino: '#8B5E3C', ice: '#5BA3CF', lava: '#D4553A', toys: '#E87DBB',
  cosmic: '#7C5BBF', food: '#E8A838', music: '#3AAFAD', art: '#C45AB3',
  ocean: '#2980B9', garden: '#27AE60', robots: '#7F8C8D', sports: '#E74C3C',
  potions: '#8E44AD', transport: '#F39C12', pirates: '#2C3E50', jungle: '#16A085',
  candy: '#E91E9C', castle: '#6C5B7B', weather: '#3498DB', carnival: '#D35400',
  bugs: '#A3CB38',
};

/* ------------------------------------------------------------------ */
/*  Modals                                                            */
/* ------------------------------------------------------------------ */

const Overlay: React.FC<{ onClose: () => void; children: React.ReactNode }> = ({ onClose, children }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
  >
    <div onClick={(e) => e.stopPropagation()}>{children}</div>
  </div>
);

const Lightbox: React.FC<{
  src: string;
  alt: string;
  s3Key: string;
  versions: AssetVersion[];
  versionsLoading: boolean;
  onClose: () => void;
  onRegenerate: (key: string) => void;
  onDelete: (key: string) => void;
  onRestore: (versionKey: string) => void;
}> = ({ src, alt, s3Key, versions, versionsLoading, onClose, onRegenerate, onDelete, onRestore }) => (
  <Overlay onClose={onClose}>
    <div style={{ background: '#1e293b', borderRadius: 16, padding: 16, maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', overflowY: 'auto' }}>
      <div style={{ display: 'flex', gap: 8, width: '100%', justifyContent: 'flex-end' }}>
        <button
          onClick={() => { onClose(); onRegenerate(s3Key); }}
          style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}
        >
          <FiRefreshCw size={14} /> Regenerate
        </button>
        <button
          onClick={() => { onClose(); onDelete(s3Key); }}
          style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}
        >
          <FiTrash2 size={14} /> Delete
        </button>
        <button onClick={onClose} style={{ padding: '6px 8px', borderRadius: 8, border: 'none', background: '#475569', color: '#fff', cursor: 'pointer' }}>
          <FiX size={16} />
        </button>
      </div>
      <img src={src} alt={alt} style={{ maxWidth: '80vw', maxHeight: versions.length > 0 ? '55vh' : '75vh', objectFit: 'contain', borderRadius: 8 }} />
      <span style={{ fontSize: 11, color: '#94a3b8', wordBreak: 'break-all' }}>{s3Key}</span>

      {/* Version History */}
      {versionsLoading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#94a3b8', fontSize: 12 }}>
          <FiLoader size={12} style={{ animation: 'spin 1s linear infinite' }} /> Loading version history...
        </div>
      )}
      {versions.length > 0 && (
        <div style={{ width: '100%', borderTop: '1px solid #334155', paddingTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, color: '#cbd5e1', fontSize: 13, fontWeight: 600 }}>
            <FiClock size={14} /> Previous Versions ({versions.length})
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {versions.map((v) => (
              <div key={v.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: '#0f172a', borderRadius: 10, padding: 8 }}>
                <img
                  src={v.url}
                  alt="previous version"
                  style={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 6, background: '#1e293b' }}
                  onError={(e) => { (e.target as HTMLImageElement).src = IMG_FALLBACK; }}
                />
                <span style={{ fontSize: 10, color: '#94a3b8' }}>{formatDate(v.timestamp)}</span>
                <span style={{ fontSize: 9, color: '#64748b' }}>{formatBytes(v.size)}</span>
                <button
                  onClick={() => onRestore(v.key)}
                  style={{ padding: '3px 8px', borderRadius: 6, border: 'none', background: '#475569', color: '#fff', cursor: 'pointer', fontSize: 10 }}
                >
                  Restore
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {!versionsLoading && versions.length === 0 && (
        <span style={{ fontSize: 11, color: '#475569' }}>No previous versions</span>
      )}
    </div>
  </Overlay>
);

const RegenerateModal: React.FC<{ assetKey: string; onClose: () => void; onSubmit: (key: string, feedback: string) => void }> = ({ assetKey, onClose, onSubmit }) => {
  const [feedback, setFeedback] = useState('');

  const submit = () => {
    onSubmit(assetKey, feedback.trim());
    onClose();
  };

  return (
    <Overlay onClose={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: 420, maxWidth: '90vw' }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Regenerate Asset</h3>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: '#64748b', wordBreak: 'break-all' }}>{assetKey}</p>
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="What should be different? (optional, e.g. 'make it rounder and less detailed')"
          rows={3}
          style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }}
          autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 14 }}>
            Cancel
          </button>
          <button onClick={submit} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: 14 }}>
            Generate
          </button>
        </div>
      </div>
    </Overlay>
  );
};

const RegenerateLineModal: React.FC<{
  creatureId: string;
  creatureName: string;
  stageCount: number;
  onClose: () => void;
  onSubmit: (creatureId: string, feedback: string) => void;
}> = ({ creatureId, creatureName, stageCount, onClose, onSubmit }) => {
  const [feedback, setFeedback] = useState('');

  const submit = () => {
    onSubmit(creatureId, feedback.trim());
    onClose();
  };

  return (
    <Overlay onClose={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: 440, maxWidth: '90vw' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <FiZap size={18} style={{ color: '#8b5cf6' }} />
          <h3 style={{ margin: 0, fontSize: 16 }}>Regenerate Full Line</h3>
        </div>
        <p style={{ margin: '0 0 4px', fontSize: 14, color: '#1e293b', fontWeight: 600 }}>{creatureName}</p>
        <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b' }}>
          This will regenerate all {stageCount} stage{stageCount !== 1 ? 's' : ''} with a cohesive new design.
          Stage 1 generates first, then later stages use it as a reference so the line stays visually consistent.
        </p>
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="Describe the new look (optional, e.g. 'more round and bubbly, bright colors, less detail')"
          rows={3}
          style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }}
          autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 14 }}>
            Cancel
          </button>
          <button onClick={submit} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#8b5cf6', color: '#fff', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiZap size={14} /> Regenerate All Stages
          </button>
        </div>
      </div>
    </Overlay>
  );
};

const DeleteConfirmModal: React.FC<{ assetKey: string; onClose: () => void; onConfirm: () => void }> = ({ assetKey, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const doDelete = async () => {
    setLoading(true);
    setError('');
    try {
      await postMoonrock('/api/analytics/admin/delete-asset', { key: assetKey });
      onConfirm();
    } catch (err) {
      setError((err as any).message || 'Delete failed');
      setLoading(false);
    }
  };

  return (
    <Overlay onClose={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: 400, maxWidth: '90vw' }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 16, color: '#ef4444' }}>Delete Asset?</h3>
        <p style={{ margin: '0 0 4px', fontSize: 13, color: '#475569' }}>This will permanently remove the image from S3:</p>
        <p style={{ margin: '0 0 16px', fontSize: 12, color: '#94a3b8', wordBreak: 'break-all' }}>{assetKey}</p>
        {error && <p style={{ color: '#ef4444', fontSize: 13, margin: '0 0 8px' }}>{error}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={loading} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 14 }}>
            Cancel
          </button>
          <button onClick={doDelete} disabled={loading} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontSize: 14, opacity: loading ? 0.6 : 1 }}>
            {loading ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </Overlay>
  );
};

type SuggestedCreature = {
  element: string;
  stages: number;
  stageNames: Record<string, string>;
  stageDescs: Record<string, string>;
};

const GEN_ELEMENTS = ['comet', 'solar', 'frost', 'ember', 'storm', 'shadow', 'crystal', 'nature'];

const GenerateGroupModal: React.FC<{
  group: string;
  onClose: () => void;
  onSubmit: (group: string, creatures: SuggestedCreature[]) => void;
}> = ({ group, onClose, onSubmit }) => {
  const [step, setStep] = useState<'counts' | 'names'>('counts');
  const [single, setSingle] = useState(0);
  const [double, setDouble] = useState(0);
  const [triple, setTriple] = useState(0);
  const [ideas, setIdeas] = useState('');
  const [loading, setLoading] = useState(false);
  const [creatures, setCreatures] = useState<SuggestedCreature[]>([]);
  const total = single + double + triple;

  const getSuggestions = () => {
    if (total < 1) return;
    setLoading(true);
    postMoonrock<{ creatures: SuggestedCreature[] }>('/api/analytics/admin/suggest-creatures', {
      group, single, double, triple, ideas: ideas.trim() || undefined,
    })
      .then((res) => {
        setCreatures(res.creatures || []);
        setStep('names');
      })
      .catch((err) => {
        alert('Failed to get suggestions: ' + ((err as any).message || 'unknown'));
      })
      .finally(() => setLoading(false));
  };

  const updateCreature = (idx: number, field: string, value: string) => {
    setCreatures((prev) => {
      const next = [...prev];
      const c = { ...next[idx] };
      if (field === 'element') {
        c.element = value;
      } else if (field.startsWith('name-')) {
        const stage = field.replace('name-', '');
        c.stageNames = { ...c.stageNames, [stage]: value };
      }
      next[idx] = c;
      return next;
    });
  };

  const submit = () => {
    onSubmit(group, creatures);
    onClose();
  };

  const rowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0' } as React.CSSProperties;
  const inputStyle = { width: 60, padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, textAlign: 'center' as const };
  const nameInput = { padding: '5px 8px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13, width: '100%', boxSizing: 'border-box' as const };

  return (
    <Overlay onClose={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: step === 'names' ? 520 : 440, maxWidth: '90vw', maxHeight: '85vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <FiPlus size={18} style={{ color: '#059669' }} />
          <h3 style={{ margin: 0, fontSize: 16 }}>Generate More Lunas</h3>
          {step === 'names' && (
            <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}>Step 2: Review names</span>
          )}
        </div>

        {step === 'counts' && (
          <>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b' }}>
              Generate new creatures for the <strong>{capitalize(group)}</strong> group.
            </p>
            <textarea
              value={ideas}
              onChange={(e) => setIdeas(e.target.value)}
              placeholder="Describe what you want (optional, e.g. 'ocean themed, jellyfish and seahorse vibes, bioluminescent')"
              rows={2}
              style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, resize: 'vertical', boxSizing: 'border-box', marginBottom: 12 }}
            />
            <div style={{ borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9', marginBottom: 12 }}>
              <div style={rowStyle}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: '#1e293b' }}>Single stage</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>1 image each</div>
                </div>
                <input type="number" value={single} min={0} max={20} onChange={(e) => setSingle(Math.max(0, Math.min(20, parseInt(e.target.value) || 0)))} style={inputStyle} />
              </div>
              <div style={rowStyle}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: '#1e293b' }}>2-stage evolution</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>2 images each (baby + final)</div>
                </div>
                <input type="number" value={double} min={0} max={10} onChange={(e) => setDouble(Math.max(0, Math.min(10, parseInt(e.target.value) || 0)))} style={inputStyle} />
              </div>
              <div style={rowStyle}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: '#1e293b' }}>3-stage evolution</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>3 images each (baby + mid + final)</div>
                </div>
                <input type="number" value={triple} min={0} max={10} onChange={(e) => setTriple(Math.max(0, Math.min(10, parseInt(e.target.value) || 0)))} style={inputStyle} />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>
                Total: {total} creature{total !== 1 ? 's' : ''} ({single + double * 2 + triple * 3} images)
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 14 }}>
                Cancel
              </button>
              <button
                onClick={getSuggestions}
                disabled={total < 1 || loading}
                style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: total < 1 ? '#d1d5db' : '#059669', color: '#fff', cursor: total < 1 ? 'not-allowed' : 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6, opacity: loading ? 0.7 : 1 }}
              >
                {loading ? <><FiLoader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Getting ideas...</> : <>Next: Name them</>}
              </button>
            </div>
          </>
        )}

        {step === 'names' && (
          <>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b' }}>
              Review and edit names for each creature. Evolved forms get different names at each stage.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
              {creatures.map((c, idx) => (
                <div key={idx} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#059669', background: '#ecfdf5', padding: '2px 8px', borderRadius: 99 }}>
                      {c.stages === 1 ? 'Single' : c.stages === 2 ? '2-stage' : '3-stage'}
                    </span>
                    <select
                      value={c.element}
                      onChange={(e) => updateCreature(idx, 'element', e.target.value)}
                      style={{ padding: '3px 6px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12, color: '#475569' }}
                    >
                      {GEN_ELEMENTS.map((el) => <option key={el} value={el}>{el}</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {Array.from({ length: c.stages }, (_, s) => s + 1).map((stage) => (
                      <div key={stage} style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {c.stages === 1 ? 'Name' : stage === 1 ? 'Baby' : stage === 2 && c.stages === 2 ? 'Final' : stage === 2 ? 'Mid' : 'Final'}
                        </div>
                        <input
                          value={c.stageNames[String(stage)] || ''}
                          onChange={(e) => updateCreature(idx, `name-${stage}`, e.target.value)}
                          style={nameInput}
                        />
                      </div>
                    ))}
                  </div>
                  {c.stageDescs?.["1"] && (
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6, fontStyle: 'italic' }}>
                      {Object.values(c.stageDescs).join(' / ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
              <button onClick={() => setStep('counts')} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 14 }}>
                Back
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 14 }}>
                  Cancel
                </button>
                <button onClick={submit} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#059669', color: '#fff', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FiPlus size={14} /> Generate {creatures.length}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Overlay>
  );
};

/* ------------------------------------------------------------------ */
/*  Create Asset Modal                                                */
/* ------------------------------------------------------------------ */

type CreateTab = 'creature' | 'build' | 'upload';
const ELEMENTS = ['comet', 'ember', 'frost', 'spark', 'gust', 'terra', 'solar', 'aqua', 'cosmic'];
const PLANETS = ['dino', 'ice', 'lava', 'toys', 'cosmic', 'food', 'music', 'art'];

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const DropZone: React.FC<{
  label: string;
  preview: string | null;
  onFile: (file: File) => void;
}> = ({ label, preview, onFile }) => {
  const [dragging, setDragging] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const handleDrop = (e: React.DragEvent) => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]); };
  return (
    <div
      onClick={() => inputRef.current && inputRef.current.click()}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      style={{
        border: dragging ? '2px dashed #3b82f6' : '2px dashed #d1d5db',
        borderRadius: 12, padding: preview ? 8 : 24, textAlign: 'center',
        cursor: 'pointer', background: dragging ? '#eff6ff' : '#f8fafc',
        transition: 'all 0.15s',
      }}
    >
      {preview ? (
        <img src={preview} alt="preview" style={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain', borderRadius: 8 }} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, color: '#94a3b8' }}>
          <FiUpload size={20} />
          <span style={{ fontSize: 12 }}>{label}</span>
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={(e) => { if (e.target.files && e.target.files[0]) onFile(e.target.files[0]); }} />
    </div>
  );
};

const CreateAssetModal: React.FC<{
  buildCategories: string[];
  onClose: () => void;
  onSuccess: (msg: string) => void;
}> = ({ buildCategories, onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState<CreateTab>('creature');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Creature fields
  const [cName, setCName] = useState('');
  const [cElement, setCElement] = useState('comet');
  const [cPlanet, setCPlanet] = useState('dino');
  const [cMaxStage, setCMaxStage] = useState(3);
  const [cImages, setCImages] = useState<Record<number, { file: File; preview: string; b64: string }>>({});

  // Build fields
  const [bTitle, setBTitle] = useState('');
  const [bCategory, setBCategory] = useState(buildCategories[0] || 'buildings');
  const [bPrice, setBPrice] = useState(100);
  const [bCoins, setBCoins] = useState(5);
  const [bImage, setBImage] = useState<{ file: File; preview: string; b64: string } | null>(null);

  // Upload fields
  const [uKey, setUKey] = useState('');
  const [uImage, setUImage] = useState<{ file: File; preview: string; b64: string } | null>(null);

  const handleFile = async (file: File, setter: (val: { file: File; preview: string; b64: string }) => void) => {
    const b64 = await fileToBase64(file);
    setter({ file, preview: URL.createObjectURL(file), b64 });
  };

  const handleStageFile = async (stage: number, file: File) => {
    const b64 = await fileToBase64(file);
    setCImages((prev) => ({ ...prev, [stage]: { file, preview: URL.createObjectURL(file), b64 } }));
  };

  const submitCreature = async () => {
    if (!cName.trim()) { setError('Name is required'); return; }
    setLoading(true); setError('');
    try {
      const images: Record<string, string> = {};
      for (let i = 1; i <= cMaxStage; i++) {
        if (cImages[i]) images[String(i)] = cImages[i].b64;
      }
      await postMoonrock<{ message: string }>('/api/analytics/admin/create-creature', {
        name: cName.trim(), element: cElement, planet: cPlanet, maxStage: cMaxStage, images,
      });
      onSuccess(`Created creature: ${cName.trim()}`);
      onClose();
    } catch (err) {
      setError((err as any).message || 'Failed');
      setLoading(false);
    }
  };

  const submitBuild = async () => {
    if (!bTitle.trim()) { setError('Title is required'); return; }
    if (!bImage) { setError('Image is required'); return; }
    setLoading(true); setError('');
    try {
      await postMoonrock<{ message: string }>('/api/analytics/admin/create-build-item', {
        title: bTitle.trim(), category: bCategory, price: bPrice, coins: bCoins, image: bImage.b64,
      });
      onSuccess(`Created build piece: ${bTitle.trim()}`);
      onClose();
    } catch (err) {
      setError((err as any).message || 'Failed');
      setLoading(false);
    }
  };

  const submitUpload = async () => {
    if (!uKey.trim()) { setError('S3 key path is required'); return; }
    if (!uImage) { setError('Image is required'); return; }
    setLoading(true); setError('');
    try {
      await postMoonrock<{ message: string }>('/api/analytics/admin/upload-asset', {
        key: uKey.trim(), image: uImage.b64,
      });
      onSuccess(`Uploaded: ${uKey.trim()}`);
      onClose();
    } catch (err) {
      setError((err as any).message || 'Failed');
      setLoading(false);
    }
  };

  const submit = () => {
    if (activeTab === 'creature') submitCreature();
    else if (activeTab === 'build') submitBuild();
    else submitUpload();
  };

  const tabBtnStyle = (t: CreateTab): React.CSSProperties => ({
    flex: 1, padding: '8px 0', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', background: activeTab === t ? '#3b82f6' : '#f1f5f9', color: activeTab === t ? '#fff' : '#64748b',
  });

  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 };
  const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, boxSizing: 'border-box' };
  const selectStyle: React.CSSProperties = { ...inputStyle, background: '#fff' };

  return (
    <Overlay onClose={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: 480, maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>Create New Asset</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><FiX size={18} /></button>
        </div>

        {/* Tab switcher */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 20, background: '#f1f5f9', padding: 4, borderRadius: 10 }}>
          <button onClick={() => setActiveTab('creature')} style={tabBtnStyle('creature')}>Creature</button>
          <button onClick={() => setActiveTab('build')} style={tabBtnStyle('build')}>Build Piece</button>
          <button onClick={() => setActiveTab('upload')} style={tabBtnStyle('upload')}>Custom Upload</button>
        </div>

        {error && <div style={{ background: '#fef2f2', color: '#dc2626', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>}

        {/* Creature form */}
        {activeTab === 'creature' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={labelStyle}>Name</label>
              <input value={cName} onChange={(e) => setCName(e.target.value)} placeholder="e.g. Frostpup" style={inputStyle} />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Element</label>
                <select value={cElement} onChange={(e) => setCElement(e.target.value)} style={selectStyle}>
                  {ELEMENTS.map((el) => <option key={el} value={el}>{capitalize(el)}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Planet</label>
                <select value={cPlanet} onChange={(e) => setCPlanet(e.target.value)} style={selectStyle}>
                  {PLANETS.map((pl) => <option key={pl} value={pl}>{capitalize(pl)}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Max Stages</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[1, 2, 3].map((n) => (
                  <button key={n} onClick={() => setCMaxStage(n)} style={{
                    flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    border: cMaxStage === n ? '2px solid #3b82f6' : '2px solid #e2e8f0',
                    background: cMaxStage === n ? '#eff6ff' : '#fff', color: cMaxStage === n ? '#2563eb' : '#64748b',
                  }}>
                    {n} stage{n > 1 ? 's' : ''}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Stage Images (optional, will auto-generate if empty)</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {Array.from({ length: cMaxStage }, (_, i) => i + 1).map((stage) => (
                  <div key={stage} style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4, textAlign: 'center' }}>Stage {stage}</div>
                    <DropZone
                      label={`Drop stage ${stage}`}
                      preview={cImages[stage] ? cImages[stage].preview : null}
                      onFile={(f) => handleStageFile(stage, f)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Build piece form */}
        {activeTab === 'build' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={labelStyle}>Title</label>
              <input value={bTitle} onChange={(e) => setBTitle(e.target.value)} placeholder="e.g. Crystal Tower" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Category</label>
              <select value={bCategory} onChange={(e) => setBCategory(e.target.value)} style={selectStyle}>
                {buildCategories.map((cat) => <option key={cat} value={cat}>{capitalize(cat)}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Price (coins)</label>
                <input type="number" value={bPrice} min={0} onChange={(e) => setBPrice(parseInt(e.target.value) || 0)} style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Bonus coins</label>
                <input type="number" value={bCoins} min={0} onChange={(e) => setBCoins(parseInt(e.target.value) || 0)} style={inputStyle} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Image</label>
              <DropZone label="Drop image here or click to browse" preview={bImage ? bImage.preview : null} onFile={(f) => handleFile(f, setBImage)} />
            </div>
          </div>
        )}

        {/* Custom upload form */}
        {activeTab === 'upload' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={labelStyle}>S3 Key Path</label>
              <input value={uKey} onChange={(e) => setUKey(e.target.value)} placeholder="e.g. creatures/my-creature-stage-1.png" style={inputStyle} />
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Path in the moonrock-images bucket</div>
            </div>
            <div>
              <label style={labelStyle}>Image</label>
              <DropZone label="Drop image here or click to browse" preview={uImage ? uImage.preview : null} onFile={(f) => handleFile(f, setUImage)} />
            </div>
          </div>
        )}

        {/* Submit */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} disabled={loading} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 14 }}>
            Cancel
          </button>
          <button onClick={submit} disabled={loading} style={{
            padding: '8px 20px', borderRadius: 8, border: 'none', background: loading ? '#93c5fd' : '#3b82f6',
            color: '#fff', cursor: loading ? 'not-allowed' : 'pointer', fontSize: 14,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            {loading ? <><FiLoader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Creating...</> : <><FiPlus size={14} /> Create</>}
          </button>
        </div>
      </div>
    </Overlay>
  );
};

/* ------------------------------------------------------------------ */
/*  Sub-components                                                    */
/* ------------------------------------------------------------------ */

const ImgThumb: React.FC<{
  src: string | null;
  alt: string;
  size?: number;
  regenerating?: boolean;
  freshUrl?: string;
  onClick?: () => void;
}> = ({ src, alt, size = 64, regenerating, freshUrl, onClick }) => {
  const displaySrc = freshUrl || src || IMG_FALLBACK;
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <img
        src={displaySrc}
        alt={alt}
        width={size}
        height={size}
        style={{ objectFit: 'contain', borderRadius: 8, background: '#f8fafc', cursor: src ? 'zoom-in' : 'default', opacity: regenerating ? 0.3 : 1 }}
        onError={(e) => { (e.target as HTMLImageElement).src = IMG_FALLBACK; }}
        onClick={() => { if (src && !regenerating && onClick) onClick(); }}
      />
      {regenerating && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 8,
        }}>
          <FiLoader size={20} style={{ color: '#3b82f6', animation: 'spin 1s linear infinite' }} />
        </div>
      )}
    </div>
  );
};

const GroupBadge: React.FC<{ group: string }> = ({ group }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 12,
      fontSize: 12,
      fontWeight: 600,
      color: '#fff',
      background: PLANET_COLORS[group] || '#64748b',
    }}
  >
    {capitalize(group)}
  </span>
);

const CollapsibleSection: React.FC<{ title: React.ReactNode; count: number; defaultOpen?: boolean; actions?: React.ReactNode; children?: React.ReactNode }> = ({
  title, count, defaultOpen = false, actions, children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 16, fontWeight: 600, color: '#1e293b', padding: '8px 0',
          }}
        >
          {open ? <FiChevronDown /> : <FiChevronRight />}
          {title}
          <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: 14 }}>({count})</span>
        </button>
        {actions}
      </div>
      {open && <div style={{ paddingLeft: 8 }}>{children}</div>}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Creatures Tab                                                     */
/* ------------------------------------------------------------------ */

const CreaturesTab: React.FC<{
  data: GameAssets;
  onOpenLightbox: (key: string, url: string) => void;
  onRegenerate: (key: string) => void;
  onDelete: (key: string) => void;
  onRegenLine: (creature: Creature) => void;
  onGenerateGroup: (group: string) => void;
  onAddEvolutions: (creatureId: string) => void;
  pendingEvolutions: Record<string, boolean>;
  pendingRegens: Record<string, boolean>;
  freshUrls: Record<string, string>;
}> = ({ data, onOpenLightbox, onRegenerate, onDelete, onRegenLine, onGenerateGroup, onAddEvolutions, pendingEvolutions, pendingRegens, freshUrls }) => {
  const [filter, setFilter] = useState('');
  const allGroups = [...data.planets, ...data.producerGroups];
  const grouped = allGroups.map((g) => ({
    group: g,
    isPlanet: data.planets.includes(g),
    creatures: data.creatures.filter((c) => c.planet === g),
  }));

  const filtered = filter
    ? grouped.map((g) => ({
        ...g,
        creatures: g.creatures.filter(
          (c) => c.name.toLowerCase().includes(filter) || c.creatureId.toLowerCase().includes(filter) || c.planet.toLowerCase().includes(filter)
        ),
      })).filter((g) => g.creatures.length > 0)
    : grouped;

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 300px' }}>
          <FiSearch style={{ position: 'absolute', left: 10, top: 10, color: '#94a3b8' }} />
          <input
            type="text"
            placeholder="Search Lunas..."
            value={filter}
            onChange={(e) => setFilter(e.target.value.toLowerCase())}
            style={{ width: '100%', padding: '8px 8px 8px 32px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14 }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Kpi label="Total Lunas" value={data.creatures.length} icon={<FiStar />} />
          <Kpi label="Starters" value={data.creatures.filter((c) => c.starter).length} icon={<FiStar />} tone="green" />
        </div>
      </div>

      {filtered.map((g) => (
        <CollapsibleSection
          key={g.group}
          title={<><GroupBadge group={g.group} /> {g.isPlanet ? 'Planet' : 'Producer Group'}</>}
          count={g.creatures.length}
          defaultOpen={g.creatures.length > 0}
          actions={
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onGenerateGroup(g.group); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px',
                borderRadius: 8, border: '1px solid #d1fae5', background: '#ecfdf5',
                color: '#059669', cursor: 'pointer', fontSize: 12, fontWeight: 500, marginLeft: 4,
              }}
              title={'Generate missing creature art for ' + capitalize(g.group)}
            >
              <FiPlus size={12} /> Generate More
            </button>
          }
        >
          {g.creatures.length === 0 ? (
            <p style={{ color: '#94a3b8', fontStyle: 'italic', margin: '8px 0' }}>
              No Lunas for this group yet
            </p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
              {g.creatures.map((c) => (
                <div
                  key={c.creatureId}
                  style={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 12,
                    padding: 12,
                    background: '#fff',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <div style={{ display: 'flex', gap: 6 }}>
                    {c.stages.length > 0 ? (
                      c.stages.map((s) => {
                        const k = s.image ? s3KeyFromUrl(s.image) : '';
                        return (
                          <ImgThumb
                            key={s.stage}
                            src={s.image}
                            alt={`${c.name} stage ${s.stage}`}
                            size={64}
                            regenerating={!!pendingRegens[k]}
                            freshUrl={freshUrls[k]}
                            onClick={() => { if (s.image) onOpenLightbox(k, freshUrls[k] || s.image); }}
                          />
                        );
                      })
                    ) : (
                      <ImgThumb src={null} alt={c.name} size={64} />
                    )}
                  </div>
                  <strong style={{ fontSize: 14 }}>{c.name}</strong>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <GroupBadge group={c.planet} />
                    <span style={{ fontSize: 11, color: '#64748b', padding: '2px 6px', background: '#f1f5f9', borderRadius: 8 }}>
                      {c.element}
                    </span>
                    {c.starter && (
                      <span style={{ fontSize: 11, color: '#16a34a', padding: '2px 6px', background: '#f0fdf4', borderRadius: 8 }}>
                        starter
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>
                      {c.stages.length} stage{c.stages.length !== 1 ? 's' : ''} | weight {c.wormholeWeight}
                    </span>
                    {c.stages.length > 1 && (
                      <button
                        type="button"
                        onClick={() => onRegenLine(c)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 3, padding: '2px 8px',
                          borderRadius: 6, border: 'none', background: '#ede9fe',
                          color: '#7c3aed', cursor: 'pointer', fontSize: 10, fontWeight: 600,
                        }}
                        title="Regenerate all stages with a new cohesive design"
                      >
                        <FiZap size={10} /> Regen Line
                      </button>
                    )}
                    {c.stages.length === 1 && (
                      <button
                        type="button"
                        onClick={() => onAddEvolutions(c.creatureId)}
                        disabled={!!pendingEvolutions[c.creatureId]}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 3, padding: '2px 8px',
                          borderRadius: 6, border: 'none',
                          background: pendingEvolutions[c.creatureId] ? '#d1d5db' : '#dbeafe',
                          color: pendingEvolutions[c.creatureId] ? '#9ca3af' : '#2563eb',
                          cursor: pendingEvolutions[c.creatureId] ? 'not-allowed' : 'pointer',
                          fontSize: 10, fontWeight: 600,
                        }}
                        title="Add stage 2 and 3 evolutions to this creature"
                      >
                        {pendingEvolutions[c.creatureId] ? (
                          <><FiLoader size={10} style={{ animation: 'spin 1s linear infinite' }} /> Adding...</>
                        ) : (
                          <><FiPlus size={10} /> Add Evolutions</>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Producers Tab                                                     */
/* ------------------------------------------------------------------ */

const ProducersTab: React.FC<{ data: GameAssets }> = ({ data }) => (
  <div>
    <h3 style={{ margin: '0 0 12px' }}>Planets (8)</h3>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
      {data.producers.filter((p) => p.isPlanet).map((p) => (
        <div key={p.group} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, background: '#fff' }}>
          <GroupBadge group={p.group} />
          <div style={{ marginTop: 8, fontSize: 13, color: '#475569' }}>
            {p.levels} upgrade levels
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>
            Lunas: {data.creatures.filter((c) => c.planet === p.group).length}
          </div>
        </div>
      ))}
    </div>

    <h3 style={{ margin: '0 0 12px' }}>Producer Groups (13)</h3>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
      {data.producers.filter((p) => !p.isPlanet).map((p) => (
        <div key={p.group} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, background: '#fff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <GroupBadge group={p.group} />
            {p.unlockLevel && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#94a3b8' }}>
                <FiLock size={12} /> Lv {p.unlockLevel}
              </span>
            )}
          </div>
          <div style={{ marginTop: 8, fontSize: 13, color: '#475569' }}>
            {p.levels} upgrade levels
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>
            Lunas: {data.creatures.filter((c) => c.planet === p.group).length}
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Merge Items Tab                                                   */
/* ------------------------------------------------------------------ */

const ItemsTab: React.FC<{
  data: GameAssets;
  onOpenLightbox: (key: string, url: string) => void;
  onRegenerate: (key: string) => void;
  onDelete: (key: string) => void;
  pendingRegens: Record<string, boolean>;
  freshUrls: Record<string, string>;
}> = ({ data, onOpenLightbox, onRegenerate, onDelete, pendingRegens, freshUrls }) => {
  const [filter, setFilter] = useState('');

  const filtered = filter
    ? data.itemGroups.filter((g) => g.group.includes(filter))
    : data.itemGroups;

  const renderItem = (item: MergeItem) => {
    const k = item.image ? s3KeyFromUrl(item.image) : '';
    return (
      <div key={item.type} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 72, gap: 2 }} title={`${item.name} (level ${item.level})`}>
        <ImgThumb
          src={item.image}
          alt={item.name}
          size={48}
          regenerating={!!pendingRegens[k]}
          freshUrl={freshUrls[k]}
          onClick={() => { if (item.image) onOpenLightbox(k, freshUrls[k] || item.image); }}
        />
        <span style={{ fontSize: 10, color: '#475569', textAlign: 'center', lineHeight: 1.2 }}>{item.name}</span>
        <span style={{ fontSize: 9, color: '#94a3b8' }}>L{item.level}</span>
      </div>
    );
  };

  return (
    <div>
      <div style={{ position: 'relative', marginBottom: 16, maxWidth: 400 }}>
        <FiSearch style={{ position: 'absolute', left: 10, top: 10, color: '#94a3b8' }} />
        <input
          type="text"
          placeholder="Filter groups..."
          value={filter}
          onChange={(e) => setFilter(e.target.value.toLowerCase())}
          style={{ width: '100%', padding: '8px 8px 8px 32px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14 }}
        />
      </div>

      {filtered.map((g) => (
        <CollapsibleSection key={g.group} title={<GroupBadge group={g.group} />} count={g.normal.length + g.alternative.length}>
          <div style={{ marginBottom: 12 }}>
            <h4 style={{ margin: '0 0 8px', fontSize: 13, color: '#64748b' }}>Normal ({g.normal.length})</h4>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {g.normal.map(renderItem)}
            </div>
          </div>
          <div>
            <h4 style={{ margin: '0 0 8px', fontSize: 13, color: '#64748b' }}>Alternative ({g.alternative.length})</h4>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {g.alternative.map(renderItem)}
            </div>
          </div>
        </CollapsibleSection>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Build Island Tab                                                  */
/* ------------------------------------------------------------------ */

const BuildTab: React.FC<{
  data: GameAssets;
  onOpenLightbox: (key: string, url: string) => void;
  onRegenerate: (key: string) => void;
  onDelete: (key: string) => void;
  pendingRegens: Record<string, boolean>;
  freshUrls: Record<string, string>;
}> = ({ data, onOpenLightbox, onRegenerate, onDelete, pendingRegens, freshUrls }) => {
  const [filter, setFilter] = useState('');

  return (
    <div>
      <div style={{ position: 'relative', marginBottom: 16, maxWidth: 400 }}>
        <FiSearch style={{ position: 'absolute', left: 10, top: 10, color: '#94a3b8' }} />
        <input
          type="text"
          placeholder="Search build items..."
          value={filter}
          onChange={(e) => setFilter(e.target.value.toLowerCase())}
          style={{ width: '100%', padding: '8px 8px 8px 32px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14 }}
        />
      </div>

      {data.buildCatalog.map((cat) => {
        const items = filter
          ? cat.items.filter((i) => i.title.toLowerCase().includes(filter) || i.id.toLowerCase().includes(filter) || (i.planetId || '').toLowerCase().includes(filter))
          : cat.items;
        if (items.length === 0) return null;
        return (
          <CollapsibleSection key={cat.id} title={capitalize(cat.name)} count={items.length} defaultOpen>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
              {items.map((item) => {
                const k = s3KeyFromUrl(item.image);
                return (
                  <div
                    key={item.id}
                    style={{
                      border: '1px solid #e2e8f0', borderRadius: 10, padding: 10, background: '#fff',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    }}
                  >
                    <ImgThumb
                      src={item.image}
                      alt={item.title}
                      size={64}
                      regenerating={!!pendingRegens[k]}
                      freshUrl={freshUrls[k]}
                      onClick={() => onOpenLightbox(k, freshUrls[k] || item.image)}
                    />
                    <strong style={{ fontSize: 12, textAlign: 'center' }}>{item.title}</strong>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {item.planetId && <GroupBadge group={item.planetId} />}
                      {item.isNew && (
                        <span style={{ fontSize: 10, color: '#059669', background: '#ecfdf5', padding: '1px 6px', borderRadius: 6 }}>new</span>
                      )}
                    </div>
                    <span style={{ fontSize: 10, color: '#94a3b8' }}>
                      {item.price.toLocaleString()} coins | +{item.coins}
                    </span>
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        );
      })}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main Page                                                         */
/* ------------------------------------------------------------------ */

const MoonrockGameAssets: React.FC = () => {
  const [tab, setTab] = useState<Tab>('creatures');
  const [regenKey, setRegenKey] = useState<string | null>(null);
  const [deleteKey, setDeleteKey] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [pendingRegens, setPendingRegens] = useState<Record<string, boolean>>({});
  const [freshUrls, setFreshUrls] = useState<Record<string, string>>({});

  // Lightbox state (lifted from ImgThumb)
  const [lightboxData, setLightboxData] = useState<{ key: string; url: string } | null>(null);
  const [lightboxVersions, setLightboxVersions] = useState<AssetVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  // Regen line modal
  const [regenLineCreature, setRegenLineCreature] = useState<Creature | null>(null);

  // Generate group modal
  const [generateGroupName, setGenerateGroupName] = useState<string | null>(null);

  // Add evolutions
  const [pendingEvolutions, setPendingEvolutions] = useState<Record<string, boolean>>({});

  // Create asset modal
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data, loading, error, reload } = useCachedQuery<GameAssets>(
    'mr:game-assets',
    (signal) => fetchMoonrock('/api/analytics/admin/game-assets', {}, signal),
    120000,
  );

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }, []);

  const handleRegenerate = useCallback((key: string) => setRegenKey(key), []);
  const handleDelete = useCallback((key: string) => setDeleteKey(key), []);

  const handleOpenLightbox = useCallback((key: string, url: string) => {
    setLightboxData({ key, url });
    setLightboxVersions([]);
    setVersionsLoading(true);
    fetchMoonrock<{ versions: AssetVersion[] }>('/api/analytics/admin/asset-versions', { key })
      .then((res) => setLightboxVersions(res.versions || []))
      .catch(() => setLightboxVersions([]))
      .finally(() => setVersionsLoading(false));
  }, []);

  const handleRestore = useCallback((versionKey: string) => {
    if (!lightboxData) return;
    const originalKey = lightboxData.key;
    setLightboxData(null);
    setPendingRegens((prev) => ({ ...prev, [originalKey]: true }));
    showToast(`Restoring previous version of ${originalKey.split('/').pop()}...`);
    postMoonrock<{ url: string }>('/api/analytics/admin/restore-asset', { key: originalKey, versionKey })
      .then((result) => {
        setFreshUrls((prev) => ({ ...prev, [originalKey]: result.url + '?t=' + Date.now() }));
        showToast(`Restored: ${originalKey.split('/').pop()}`);
      })
      .catch((err) => {
        showToast(`Restore failed: ${(err as any).message || 'unknown error'}`);
      })
      .finally(() => {
        setPendingRegens((prev) => { const next = { ...prev }; delete next[originalKey]; return next; });
      });
  }, [lightboxData, showToast]);

  const fireRegenerate = useCallback((key: string, feedback: string) => {
    setPendingRegens((prev) => ({ ...prev, [key]: true }));
    showToast(`Regenerating ${key.split('/').pop()}...`);
    postMoonrock<{ url: string }>('/api/analytics/admin/regenerate-asset', {
      key,
      feedback: feedback || undefined,
    })
      .then((result) => {
        setFreshUrls((prev) => ({ ...prev, [key]: result.url + '?t=' + Date.now() }));
        showToast(`Done: ${key.split('/').pop()}`);
      })
      .catch((err) => {
        showToast(`Failed: ${(err as any).message || 'unknown error'}`);
      })
      .finally(() => {
        setPendingRegens((prev) => { const next = { ...prev }; delete next[key]; return next; });
      });
  }, [showToast]);

  const fireRegenerateLine = useCallback((creatureId: string, feedback: string) => {
    if (!data) return;
    const creature = data.creatures.find((c) => c.creatureId === creatureId);
    if (!creature) return;
    const stageKeys = creature.stages.map((s) => s3KeyFromUrl(s.image));
    stageKeys.forEach((k) => {
      if (k) setPendingRegens((prev) => ({ ...prev, [k]: true }));
    });
    showToast(`Regenerating all ${creature.stages.length} stages of ${creature.name}...`);
    postMoonrock<{ stages: Array<{ key: string; url: string }> }>('/api/analytics/admin/regenerate-line', {
      creatureId,
      feedback: feedback || undefined,
    })
      .then((result) => {
        const urls: Record<string, string> = {};
        (result.stages || []).forEach((s) => {
          urls[s.key] = s.url + '?t=' + Date.now();
        });
        setFreshUrls((prev) => ({ ...prev, ...urls }));
        showToast(`Done: all stages of ${creature.name}`);
      })
      .catch((err) => {
        showToast(`Line regen failed: ${(err as any).message || 'unknown error'}`);
      })
      .finally(() => {
        stageKeys.forEach((k) => {
          if (k) setPendingRegens((prev) => { const next = { ...prev }; delete next[k]; return next; });
        });
      });
  }, [data, showToast]);

  const fireGenerateGroup = useCallback((group: string, creatures: SuggestedCreature[]) => {
    const imgCount = creatures.reduce((sum, c) => sum + (c.stages || 1), 0);
    showToast(`Starting generation of ${imgCount} ${capitalize(group)} creature images...`);
    postMoonrock<{ message: string }>('/api/analytics/admin/generate-group', { group, creatures })
      .then((result) => {
        showToast(result.message || `Generation started for ${group}`);
      })
      .catch((err) => {
        showToast(`Generate failed: ${(err as any).message || 'unknown error'}`);
      });
  }, [showToast]);

  const fireAddEvolutions = useCallback((creatureId: string) => {
    setPendingEvolutions((prev) => ({ ...prev, [creatureId]: true }));
    showToast(`Adding evolutions for ${creatureId}...`);
    postMoonrock<{ message: string; stageNames: Record<string, string> }>('/api/analytics/admin/add-evolutions', { creatureId })
      .then((result) => {
        showToast(result.message || `Evolutions added for ${creatureId}`);
        reload();
      })
      .catch((err) => {
        showToast(`Failed: ${(err as any).message || 'unknown error'}`);
      })
      .finally(() => {
        setPendingEvolutions((prev) => { const next = { ...prev }; delete next[creatureId]; return next; });
      });
  }, [showToast, reload]);

  return (
    <div className="page-content">
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <PageHeader
          title="Game Assets"
          description="Browse all Lunas, producers, merge items, and build island pieces. Click any image to view, regenerate, or delete."
        />
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px',
            borderRadius: 10, border: 'none', background: '#3b82f6', color: '#fff',
            fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', marginTop: 4,
          }}
        >
          <FiPlus size={16} /> Create New
        </button>
      </div>

      {error && <Notice tone="error">Failed to load game assets. Check the API connection.</Notice>}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 10000,
          background: '#1e293b', color: '#fff', padding: '12px 20px',
          borderRadius: 12, fontSize: 14, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>
          {toast}
        </div>
      )}

      {/* Lightbox with version history */}
      {lightboxData && (
        <Lightbox
          src={lightboxData.url}
          alt={lightboxData.key}
          s3Key={lightboxData.key}
          versions={lightboxVersions}
          versionsLoading={versionsLoading}
          onClose={() => setLightboxData(null)}
          onRegenerate={(key) => { setLightboxData(null); handleRegenerate(key); }}
          onDelete={(key) => { setLightboxData(null); handleDelete(key); }}
          onRestore={handleRestore}
        />
      )}

      {/* Regenerate single asset modal */}
      {regenKey && (
        <RegenerateModal
          assetKey={regenKey}
          onClose={() => setRegenKey(null)}
          onSubmit={fireRegenerate}
        />
      )}

      {/* Regenerate line modal */}
      {regenLineCreature && (
        <RegenerateLineModal
          creatureId={regenLineCreature.creatureId}
          creatureName={regenLineCreature.name}
          stageCount={regenLineCreature.stages.length}
          onClose={() => setRegenLineCreature(null)}
          onSubmit={fireRegenerateLine}
        />
      )}

      {/* Generate group modal */}
      {generateGroupName && (
        <GenerateGroupModal
          group={generateGroupName}
          onClose={() => setGenerateGroupName(null)}
          onSubmit={fireGenerateGroup}
        />
      )}

      {/* Create asset modal */}
      {showCreateModal && (
        <CreateAssetModal
          buildCategories={data ? data.buildCatalog.map((c) => c.name || c.id) : ['buildings', 'decorations', 'paths', 'nature', 'special']}
          onClose={() => setShowCreateModal(false)}
          onSuccess={(msg) => { showToast(msg); setTimeout(() => reload(), 2000); }}
        />
      )}

      {/* Delete confirm modal */}
      {deleteKey && (
        <DeleteConfirmModal
          assetKey={deleteKey}
          onClose={() => setDeleteKey(null)}
          onConfirm={() => {
            setDeleteKey(null);
            showToast(`Deleted: ${deleteKey.split('/').pop()}`);
            reload();
          }}
        />
      )}

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0', marginBottom: 20 }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderBottom: tab === t.key ? '2px solid #3b82f6' : '2px solid transparent',
              marginBottom: -2,
              background: 'none',
              cursor: 'pointer',
              fontWeight: tab === t.key ? 600 : 400,
              color: tab === t.key ? '#1e293b' : '#94a3b8',
              fontSize: 14,
            }}
          >
            {t.label}
            {data && (
              <span style={{ marginLeft: 6, fontSize: 12, color: '#94a3b8' }}>
                {t.key === 'creatures' && data.creatures.length}
                {t.key === 'producers' && data.producers.length}
                {t.key === 'items' && data.itemGroups.reduce((n, g) => n + g.normal.length + g.alternative.length, 0)}
                {t.key === 'build' && data.buildCatalog.reduce((n, c) => n + c.items.length, 0)}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading && !data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Skeleton width="100%" height="40px" />
          <Skeleton width="100%" height="200px" />
          <Skeleton width="100%" height="200px" />
        </div>
      )}

      {data && tab === 'creatures' && (
        <CreaturesTab
          data={data}
          onOpenLightbox={handleOpenLightbox}
          onRegenerate={handleRegenerate}
          onDelete={handleDelete}
          onRegenLine={(c) => setRegenLineCreature(c)}
          onGenerateGroup={(g) => setGenerateGroupName(g)}
          onAddEvolutions={fireAddEvolutions}
          pendingEvolutions={pendingEvolutions}
          pendingRegens={pendingRegens}
          freshUrls={freshUrls}
        />
      )}
      {data && tab === 'producers' && <ProducersTab data={data} />}
      {data && tab === 'items' && (
        <ItemsTab
          data={data}
          onOpenLightbox={handleOpenLightbox}
          onRegenerate={handleRegenerate}
          onDelete={handleDelete}
          pendingRegens={pendingRegens}
          freshUrls={freshUrls}
        />
      )}
      {data && tab === 'build' && (
        <BuildTab
          data={data}
          onOpenLightbox={handleOpenLightbox}
          onRegenerate={handleRegenerate}
          onDelete={handleDelete}
          pendingRegens={pendingRegens}
          freshUrls={freshUrls}
        />
      )}
    </div>
  );
};

export default MoonrockGameAssets;
